import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  PlayIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../stores/authStore';
import { api } from '../../../services/api';
import toast from 'react-hot-toast';

interface Exercise {
  _id: string;
  legacyId?: number;
  name: string;
  description: string;
  musclesWorked: string[];
  category?: string;
  difficulty?: string;
  equipment?: string[];
  videoUrl?: string;
  imageUrl?: string;
  estimatedDuration?: number;
  caloriesBurned?: number;
  approved?: boolean;
  isPublic?: boolean;
  tags?: string[];
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Cache for exercises data
let exercisesCache: Exercise[] | null = null;
let exercisesCacheTimestamp: number | null = null;
let exercisesPromise: Promise<Exercise[]> | null = null; // Prevent duplicate requests
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const ExerciseLibrary: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [allExercises, setAllExercises] = useState<Exercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<Exercise | null>(null);

  const isConsultant = user?.roles?.includes('CONSULTANT');

  // Fetch all exercises with caching and duplicate request prevention
  const fetchAllExercises = useCallback(async () => {
    // Check if we have valid cached data
    const now = Date.now();
    if (exercisesCache && exercisesCacheTimestamp && (now - exercisesCacheTimestamp) < CACHE_DURATION) {
      setAllExercises(exercisesCache);
      setFilteredExercises(exercisesCache);
      setLoading(false);
      return;
    }

    // If there's already a request in progress, wait for it
    if (exercisesPromise) {
      try {
        const exercises = await exercisesPromise;
        setAllExercises(exercises);
        setFilteredExercises(exercises);
        setLoading(false);
        return;
      } catch (error) {
        // If the existing promise failed, we'll make a new request below
        exercisesPromise = null;
      }
    }

    try {
      setLoading(true);
      
      // Create a new promise for this request
      exercisesPromise = api.get('/libraries/exercises').then((response: any) => {
        const exercises = response.data.exercises || [];
        
        // Update cache
        exercisesCache = exercises;
        exercisesCacheTimestamp = now;
        exercisesPromise = null; // Clear the promise
        
        return exercises;
      });
      
      const exercises = await exercisesPromise;
      setAllExercises(exercises || []);
      setFilteredExercises(exercises || []);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
      toast.error('Failed to load exercises');
      setAllExercises([]);
      setFilteredExercises([]);
      exercisesPromise = null; // Clear the promise on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllExercises();
  }, [fetchAllExercises]);

  // Client-side filtering
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) {
      setFilteredExercises(allExercises);
      return;
    }

    const filtered = allExercises.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.musclesWorked?.some(muscle => muscle.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredExercises(filtered);
  }, [searchTerm, allExercises]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Update URL for meaningful searches
    if (value.length === 0 || value.length >= 3) {
      const newParams = new URLSearchParams(searchParams);
      if (value && value.length >= 3) {
        newParams.set('search', value);
      } else {
        newParams.delete('search');
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
    }
  };

  const toggleExpanded = (itemId: string) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      setEditingItem(null);
    } else {
      setExpandedItem(itemId);
      const item = allExercises.find(e => e._id === itemId);
      if (item && isConsultant) {
        setEditingItem({ ...item });
      }
    }
  };

  const handleInputChange = (field: keyof Exercise, value: any) => {
    if (editingItem) {
      setEditingItem({
        ...editingItem,
        [field]: value
      });
    }
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await api.put(`/libraries/exercises/${editingItem._id}`, editingItem);
      toast.success('Exercise updated successfully');
      
      // Update local data and cache
      const updatedExercises = allExercises.map(item =>
        item._id === editingItem._id ? editingItem : item
      );
      setAllExercises(updatedExercises);
      
      // Update cache as well
      if (exercisesCache) {
        exercisesCache = updatedExercises;
      }
      
      setExpandedItem(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update exercise:', error);
      toast.error('Failed to update exercise');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setExpandedItem(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/libraries" className="text-blue-600 text-sm font-medium">
            ← Libraries
          </Link>
          {isConsultant && (
            <button
              onClick={() => {/* TODO: Add new exercise modal */}}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium"
            >
              + Add
            </button>
          )}
        </div>
        <h1 className="text-xl font-bold text-gray-900 mt-2">Exercises</h1>
        <p className="text-sm text-gray-600">
          {searchTerm && searchTerm.length >= 3 
            ? `${filteredExercises.length} of ${allExercises.length} items`
            : `${allExercises.length} items`
          }
        </p>
      </div>

      {/* Mobile Search - Sticky positioned below top bar */}
      <div className="sticky top-16 z-30 px-4 py-3 bg-white border-b border-gray-200 shadow-lg">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={searchTerm.length > 0 && searchTerm.length < 3 ? "Type 3+ characters to search..." : "Search exercises..."}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white ${
              searchTerm.length > 0 && searchTerm.length < 3 
                ? 'bg-yellow-50 text-yellow-700' 
                : 'bg-gray-50'
            }`}
          />
        </div>
      </div>

      {/* Exercise List */}
      {filteredExercises.length === 0 ? (
        <div className="text-center py-8 px-4">
          <PlayIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No exercises found</h3>
          <p className="mt-1 text-xs text-gray-500">
            {searchTerm
              ? 'Try adjusting your search'
              : 'Get started by adding exercises.'}
          </p>
        </div>
      ) : (
        <div className="bg-white divide-y divide-gray-100">
          {filteredExercises.map((item) => (
            <div key={item._id}>
              {/* Mobile List Item */}
              <div
                onClick={() => toggleExpanded(item._id)}
                className="px-4 py-3 active:bg-gray-50 touch-manipulation"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    {item.videoUrl && (
                      <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    )}
                    {isConsultant && (
                      <PencilIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile Expanded Section */}
              {expandedItem === item._id && (
                <div className="px-4 py-4 bg-gray-50 border-t">
                  {isConsultant && editingItem ? (
                    /* Mobile Edit Form */
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editingItem.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={editingItem.category || ''}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Difficulty</label>
                          <select
                            value={editingItem.difficulty || ''}
                            onChange={(e) => handleInputChange('difficulty', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select</option>
                            <option value="BEGINNER">Beginner</option>
                            <option value="INTERMEDIATE">Intermediate</option>
                            <option value="ADVANCED">Advanced</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editingItem.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Muscles Worked</label>
                        <input
                          type="text"
                          value={editingItem.musclesWorked?.join(', ') || ''}
                          onChange={(e) => handleInputChange('musclesWorked', e.target.value.split(',').map(s => s.trim()))}
                          placeholder="Chest, Shoulders, Triceps"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Video URL</label>
                        <input
                          type="url"
                          value={editingItem.videoUrl || ''}
                          onChange={(e) => handleInputChange('videoUrl', e.target.value)}
                          placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Duration (min)</label>
                          <input
                            type="number"
                            value={editingItem.estimatedDuration || ''}
                            onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Calories</label>
                          <input
                            type="number"
                            value={editingItem.caloriesBurned || ''}
                            onChange={(e) => handleInputChange('caloriesBurned', parseInt(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Status</label>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.approved || false}
                              onChange={(e) => handleInputChange('approved', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Approved</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isPublic || false}
                              onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Public</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={handleCancel}
                          className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white active:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          className="flex-1 py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 active:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Mobile View Details */
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <span className="text-xs font-medium text-gray-500">Muscles</span>
                        <div className="text-right">
                          {item.musclesWorked?.map((muscle, index) => (
                            <span key={index} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs mr-1 mb-1">
                              {muscle}
                            </span>
                          ))}
                        </div>
                      </div>

                      {(item.category || item.difficulty) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {item.category && (
                            <div>
                              <span className="text-xs text-gray-500 block">Category</span>
                              <span className="text-gray-900">{item.category}</span>
                            </div>
                          )}
                          {item.difficulty && (
                            <div>
                              <span className="text-xs text-gray-500 block">Difficulty</span>
                              <span className="text-gray-900">{item.difficulty}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {(item.estimatedDuration || item.caloriesBurned) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {item.estimatedDuration && (
                            <div>
                              <span className="text-xs text-gray-500 block">Duration</span>
                              <span className="text-gray-900">{item.estimatedDuration} min</span>
                            </div>
                          )}
                          {item.caloriesBurned && (
                            <div>
                              <span className="text-xs text-gray-500 block">Calories</span>
                              <span className="text-gray-900">{item.caloriesBurned}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {item.description && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Description</span>
                          <p className="text-sm text-gray-900">{item.description}</p>
                        </div>
                      )}

                      {item.videoUrl && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Video</span>
                          <a 
                            href={item.videoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 active:text-blue-900"
                          >
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                            </svg>
                            Watch Exercise Video
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExerciseLibrary;